import { PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';

import { BasicModalsContext } from './BasicModalsContext';
import { AlertModal, ConfirmModal, PromptModal } from './Modals';
import {
  AlertOptions,
  ConfirmOptions,
  DialogOptions,
  OmitDialogOptionsType,
  PromptOptions,
} from './types';

export const BasicModalsProvider = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<DialogOptions | null>(null);

  const resultRef = useRef<{
    resolve: (payload?: unknown) => void;
    reject: () => void;
  }>(undefined);

  const openDialog = <TOptions extends DialogOptions, TPayload>(options: TOptions) => {
    setOpen(true);
    setDialog(options);

    return new Promise<TPayload>((resolve, reject) => {
      resultRef.current = { resolve, reject } as any;
    });
  };

  const openAlert = useCallback(
    (options: OmitDialogOptionsType<AlertOptions>) =>
      openDialog<AlertOptions, void>({ ...options, type: 'alert' }),
    []
  );

  const openConfirm = useCallback(
    (options: OmitDialogOptionsType<ConfirmOptions>) =>
      openDialog<ConfirmOptions, boolean>({ ...options, type: 'confirm' }),
    []
  );

  const openPrompt = useCallback(
    (options: OmitDialogOptionsType<PromptOptions>) =>
      openDialog<PromptOptions, string>({ ...options, type: 'prompt' }),
    []
  );

  const handleClose = (result?: string | boolean) => {
    resultRef.current?.resolve(result);
    setOpen(false);
    // allow close animation
    setTimeout(() => setDialog(null), 200);
  };

  const handleYes = () => handleClose(true);
  const handleNo = () => handleClose(false);

  const value = useMemo(
    () => ({
      alert: openAlert,
      confirm: openConfirm,
      prompt: openPrompt,
    }),
    [openAlert, openConfirm, openPrompt]
  );

  return (
    <BasicModalsContext value={value}>
      {children}

      {dialog?.type === 'alert' && (
        <AlertModal {...dialog} open={open} onClose={() => handleClose()} />
      )}

      {dialog?.type === 'confirm' && (
        <ConfirmModal {...dialog} open={open} onCancel={handleNo} onConfirm={handleYes} />
      )}

      {dialog?.type === 'prompt' && (
        <PromptModal
          {...dialog}
          open={open}
          onCancel={() => handleClose()}
          onSubmit={(input) => handleClose(input)}
        />
      )}
    </BasicModalsContext>
  );
};
